import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext } from '@dnd-kit/sortable'
import { skipToken } from '@reduxjs/toolkit/query'
import { format } from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'
import { NavLink, useSearchParams } from 'react-router-dom'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import { TableComponent } from '../../components/table'
import { deepCopy, moveElement } from '../../helpers'
import { useDebounceSearch } from '../../hooks/useDeboucedSearch'
import { useModalControl } from '../../hooks/useModalControl'
import {
  useFetchListsQuery,
  usePinUnpinListMutation,
  usePublishUnpublishListMutation,
  useReorderEverydayListMutation,
} from '../../redux-toolkits/lists/list.slice'
import { SingleEssential } from '../../redux-toolkits/lists/list.types'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
const EssentialsTable: React.FC = () => {
  const [lists, setLists] = useState<SingleEssential[]>([])
  const [editing, setEditing] = useState<SingleEssential>()
  const [query, setQuery] = useState<QueryProps>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { modalOpen, handleModalClose, handleModalOpen, setModalOpen, modal } =
    useModalControl()

  const { data: listsData, isFetching } = useFetchListsQuery(
    query
      ? {
          ...query,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  )

  const [reorderList] = useReorderEverydayListMutation()
  const isUserReorder = useRef(false)

  useDebounceSearch({
    searchQuery: lists,
    onSearch: async (query) => {
      if (!isUserReorder.current) return

      isUserReorder.current = false

      const reorderListPayload = query as SingleEssential[]
      if (reorderListPayload.length > 0) {
        await reorderList({
          listIds: reorderListPayload.map((item) => item._id),
        })
      }
    },
    delay: 1000,
  })

  const [pinUnpinList, { isLoading: togglingListPin }] =
    usePinUnpinListMutation()

  const [publishUnpublishList, { isLoading: togglingPublishStatus }] =
    usePublishUnpublishListMutation()

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e

    if (over && active.id !== over.id) {
      const dataCopy: SingleEssential[] = deepCopy(lists)
      const dataCopyMod = dataCopy.map((list) => {
        return {
          id: `${list._id}`,
          ...list,
        }
      })

      const oldIndex = dataCopyMod.findIndex((item) => item.id === active.id)
      const newIndex = dataCopyMod.findIndex((item) => item.id === over.id)

      isUserReorder.current = true
      setLists(moveElement(dataCopy, oldIndex, newIndex))
    }
  }

  useEffect(() => {
    if (listsData?.data?.docs) {
      setLists(listsData.data.docs)
    }
  }, [listsData])

  useEffect(() => {
    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: 20,
      search: searchParams.get('search') || undefined,
    })
  }, [searchParams])

  return (
    <>
      <SearchInput
        searchFunction={(search) => {
          setSearchParams({
            ...Object.fromEntries(searchParams),
            search: search,
            page: '1',
          })
        }}
        placeholder="Search essentials"
        className="!w-full md:!max-w-[400px]"
        initialValue={searchParams.get('search') || undefined}
      />

      <DndContext
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={
            lists.map((list) => {
              return {
                id: `${list._id}`,
                ...list,
              }
            }) ?? []
          }
        >
          <div className="w-full overflow-x-scroll">
            <TableComponent
              sortable
              headers={['Title', 'Status', 'Author', 'Date created', 'Actions']}
              rows={
                lists.map((listDetail) => {
                  return {
                    id: listDetail._id,
                    content: [
                      <div key="" className="flex itemx-center gap-2">
                        {listDetail.pinned && (
                          <Icon
                            id="pin"
                            width={20}
                            height={20}
                            className="text-primary"
                          />
                        )}
                        <NavLink
                          to={`/lists/curated/view/${listDetail._id}`}
                          className={`
                      hover:text-black hover:underline
                      transition-all duration-500
                      `}
                        >
                          {listDetail.title}
                        </NavLink>
                      </div>,
                      <div key="">
                        <Status
                          type={
                            listDetail.status.toLocaleLowerCase() ===
                            'published'
                              ? 'success'
                              : 'warn'
                          }
                          text={listDetail.status.toLowerCase()}
                        />
                      </div>,
                      <div
                        key=""
                        className="capitalize"
                      >{`${listDetail.author.firstname} ${listDetail.author.lastname}`}</div>,
                      <div key="">
                        {format(listDetail.createdAt, 'dd/MM/yyyy h:mma')}
                      </div>,
                      <Dropdown
                        key={`${listDetail._id}-controls`}
                        menuButton={
                          <Icon
                            id="ellipses"
                            height={18}
                            width={18}
                            className=""
                          />
                        }
                        menuItems={[
                          {
                            name:
                              listDetail.status === 'DRAFT'
                                ? 'Publish list'
                                : 'Unpublish list',

                            value: '',
                            onClick: () => {
                              setEditing(listDetail)
                              handleModalOpen('publishListModal')
                            },
                          },
                          {
                            name: listDetail.pinned ? 'Unpin list' : 'Pin list',
                            value: '',
                            onClick: () => {
                              setEditing(listDetail)
                              handleModalOpen('pinListModal')
                            },
                          },
                        ]}
                      />,
                    ],
                  }
                }) ?? []
              }
              name="lists-table"
              loading={isFetching}
              isEmpty={[undefined, 0].includes(listsData?.data?.docs?.length)}
              currentPage={Number(query?.page ?? 0)}
              totalDataCount={listsData?.data?.total}
              pageLimit={Number(query?.limit ?? 0)}
              pageRangeDisplayed={3}
              onPageChange={(pageNo) => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  page: `${pageNo}`,
                })
              }}
            />

            {editing && (
              <ConfirmationModal
                open={modalOpen && modal === 'pinListModal'}
                setOpen={setModalOpen}
                message="Confirm to proceed"
                title={`${editing.pinned ? 'Unpin' : 'Pin'} "${editing.title}"`}
                onCloseCallback={() => {}}
                onConfirm={async () => {
                  const resp = await pinUnpinList({
                    body: { pinned: String(!editing.pinned) },
                    id: editing._id,
                  })
                  if (resp.data?.success) {
                    handleModalClose()
                  }
                }}
                loading={togglingListPin}
                confirmText="Confirm"
              />
            )}

            {editing && (
              <ConfirmationModal
                open={modalOpen && modal === 'publishListModal'}
                setOpen={setModalOpen}
                message="Confirm to proceed"
                title={`${
                  editing?.status === 'DRAFT' ? 'Publish' : 'Unpublish'
                } "${editing?.title}"?`}
                onCloseCallback={() => {}}
                onConfirm={async () => {
                  const resp = await publishUnpublishList({
                    id: editing._id,
                    type: editing.status === 'DRAFT' ? 'publish' : 'unpublish',
                  })

                  if (resp.data?.success) {
                    handleModalClose()
                  }
                }}
                loading={togglingPublishStatus}
                confirmText="Confirm"
              />
            )}
          </div>
        </SortableContext>
      </DndContext>
    </>
  )
}

export default React.memo(EssentialsTable)
